import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import tinycolor from 'tinycolor2';
import { useTranslation } from 'react-i18next';

import { EDITOR_PLAYER_ID, isAllowAutoPlay, isTouchable, isUsedOnMobile, STORY_CONFIG_ID } from '../../util/util';

import * as QPlayerSVGElement from '../QPlayerSVGElement';
import ControlButton from '../Controls/ControlButton';
import MediaItem from './media/MediaItem';
import { FetchDataHelper } from '../Player/hooks/fetch-data-helper';
import { setMediasData } from '../../actions/configurationActions';
import { setPlayerCurrentPosition } from '../../actions/playerActions';
import useFetchPlaylistData from '../Player/hooks/useFetchPlaylistData';

import './Playlist.scss';

interface PlaylistProps {
	playlistId: string;
	settings: any;
	playerHover: boolean;
	queryParams: any;
	onMouseEnter?: () => {};
	onMouseLeave?: () => {};
}

const Playlist = ({ playlistId, settings, playerHover, queryParams, onMouseEnter, onMouseLeave }: PlaylistProps) => {
	const { t: translator } = useTranslation();
	const dispatch = useDispatch();

	const { isPosterRemoved, isPlaying } = useSelector((state) => (state as any).playerReducer);
	const { showCountdownModule, currentMediaId, accountId, mediasData } = useSelector((state) => (state as any).configurationReducer);
	const [isPlaylistExpand, setPlaylistExpand] = useState(false);
	const [playlistMedias, setPlaylistMedias] = useState<any[]>([]);

	const playlistData = useFetchPlaylistData({ playlistId, accountId });

	useEffect(() => {
		if (!playlistData || mediasData.every((item: any) => !!item.playlistAsset)) {
			return;
		}
		const newMediasData = mediasData.map((item: any) => ({ ...item, playlistAsset: playlistData.asset }));
		dispatch(setMediasData(newMediasData));
	}, [playlistId, playlistData, mediasData, dispatch]);

	const primaryColor = settings?.playerStyling?.primaryColor ?? '#126cfc';
	const opacity = Number(settings?.playerStyling?.primaryOpacity ?? 100);
	const rgbaWithOpa = { ...tinycolor(primaryColor).toRgb(), a: opacity / 100 };
	const hoverStyle = isTouchable() ? {} : { backgroundColor: tinycolor(rgbaWithOpa).toRgbString() };
	const tabIndexValue = isUsedOnMobile(queryParams) ? -1 : 0;
	const solidColor = settings?.bottomControls?.solidBackgroundBottomColor ?? '#000000';
	const solidColorStyle = settings.bottomControls.showSolidBackgroundBottomControl
		? {
				backgroundColor: tinycolor({
					...tinycolor(solidColor).toRgb(),
					a: Number(settings.bottomControls.solidBackgroundBottomControlOpacity ?? 100) / 100,
				}).toRgbString(),
			}
		: { backgroundColor: '#00000066' };

	const playerId = `player-${playlistId}`;
	const currentMediaIndex = playlistMedias.findIndex((item: any) => item.id === currentMediaId);
	const language = queryParams?.language ? queryParams?.language : settings?.accessibilitySettings?.language;
	const publisher = settings.topControls?.accountName?.[language] || settings.topControls?.accountName?.['all'] || '';
	const currentMediaThumbnail = playlistMedias?.[currentMediaIndex]?.resources?.[0]?.posters?.[0]?.url;
	const playlistTitle = playlistData?.metadata?.title || 'Video Playlist';
	const isStoryPlaylist = settings?.id === STORY_CONFIG_ID;

	const handlePlayPreviousMedia = () => {
		if (currentMediaIndex > 0) {
			handlePlaySelectedMedia(currentMediaIndex - 1);
		}
	};

	const handlePlayNextMedia = () => {
		if (currentMediaIndex !== playlistMedias.length - 1) {
			(window as any).GoBrain.widgets(playerId)?.Queue?.next();
			dispatch(setPlayerCurrentPosition(0));
		}
	};

	const handlePlaySelectedMedia = useCallback(
		(index) => {
			(window as any).GoBrain.widgets(playerId)?.Queue?.select(index);
			dispatch(setPlayerCurrentPosition(0));
		},
		[dispatch, playerId]
	);

	const isAllowToAutoPlay = playerId === EDITOR_PLAYER_ID || isAllowAutoPlay({ queryParams, settings });
	const showPoster = !isPosterRemoved && !isPlaying && !isAllowToAutoPlay;

	useEffect(() => {
		if (playlistMedias.length > 0) {
			return;
		}

		// For story playlist, we're not initializing gobrain playlist widget, therefore get media data from playlistEndpoint
		if (isStoryPlaylist && playlistData?.medias?.length) {
			const newMediasData = playlistData.medias.map((media: any, currentIndex: number) => {
				return FetchDataHelper.getMediaData({
					currentIndex,
					mediaData: media.item,
					id: media?.id,
				});
			});
			dispatch(setMediasData(newMediasData));
			setPlaylistMedias(newMediasData ?? []);
			return;
		}

		// Normal playlist
		const widget = (window as any).GoBrain.widgets()[playerId];
		const { queue } = widget?.Queue?.get() ?? {};
		if (queue && queue.length) {
			const newMediasData = queue.map((item: any, currentIndex: number) =>
				FetchDataHelper.getMediaData({
					currentIndex,
					mediaData: item,
					id: item?.id,
				})
			);
			dispatch(setMediasData(newMediasData));
		}
		setPlaylistMedias(queue ?? []);
	}, [playerId, dispatch, currentMediaId, isStoryPlaylist, playlistData]);

	if (showCountdownModule || showPoster) {
		return null;
	}

	if (settings.id === STORY_CONFIG_ID) {
		return null;
	}

	return playlistMedias.length ? (
		<div
			className={`playlist-container ${!playerHover ? 'invisible' : ''} ${isPlaylistExpand ? 'playlist-expanded' : ''}`}
			style={solidColorStyle}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
		>
			<div className="playlist-infomation">
				{currentMediaThumbnail ? (
					<img className="current-media-thumbnail" src={currentMediaThumbnail} />
				) : (
					<div className="current-media-thumbnail" style={{ backgroundColor: primaryColor }} />
				)}

				<div className="playlist-metadata">
					<div className="playlist-title">{playlistTitle}</div>
					<div className="publisher-name">{publisher}</div>
				</div>
			</div>
			<div className="media-control">
				<ControlButton
					tabIndex={tabIndexValue}
					ariaLabel={isPlaylistExpand ? translator('LABEL_CLOSE') : translator('LABEL_OPEN')}
					className="playlist-control-btn"
					hoverStyle={hoverStyle}
					onClick={() => {
						setPlaylistExpand(!isPlaylistExpand);
					}}
					tooltip={isPlaylistExpand ? translator('LABEL_CLOSE') : translator('LABEL_OPEN')}
					icon={isPlaylistExpand ? <QPlayerSVGElement.Collapse /> : <QPlayerSVGElement.Expand />}
				/>

				<div className="stats">
					<div className="media-number">
						{isPlaylistExpand ? `${playlistMedias.length} video(s)` : `${currentMediaIndex + 1} of ${playlistMedias.length}`}
					</div>
					<div className="media-title">{isPlaylistExpand ? '' : playlistMedias[currentMediaIndex]?.title}</div>
				</div>
				<div className="cta-btn">
					<ControlButton
						tabIndex={tabIndexValue}
						ariaLabel={translator('LABEL_PLAY_PREVIOUS_MEDIA')}
						className={`playlist-control-btn ${currentMediaIndex === 0 ? 'disabled' : ''}`}
						hoverStyle={hoverStyle}
						onClick={handlePlayPreviousMedia}
						tooltip={translator('LABEL_PLAY_PREVIOUS_MEDIA')}
						icon={<QPlayerSVGElement.Previous />}
					/>
					<ControlButton
						tabIndex={tabIndexValue}
						ariaLabel={translator('LABEL_PLAY_NEXT_MEDIA')}
						className={`playlist-control-btn ${currentMediaIndex === playlistMedias.length - 1 ? 'disabled' : ''}`}
						hoverStyle={hoverStyle}
						onClick={handlePlayNextMedia}
						tooltip={translator('LABEL_PLAY_NEXT_MEDIA')}
						icon={<QPlayerSVGElement.Next />}
					/>
				</div>
			</div>
			{isPlaylistExpand && (
				<div className="media-list">
					{playlistMedias.map((item: any, idx: number) => (
						<MediaItem
							key={item.id}
							data={item}
							isPlaying={item.id === currentMediaId}
							primaryColor={primaryColor}
							onClick={() => {
								handlePlaySelectedMedia(idx);
							}}
						/>
					))}
				</div>
			)}
		</div>
	) : null;
};

export default Playlist;
